import React, { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import queryString from "query-string";
import Grid from "@material-ui/core/Grid";
import { Button, showToast, BackTo } from "@deeptrue-frontend/ui";

import { Dashboard } from "components/Layout";
import api from "api/api";
import StoreProvider from "components/StoreProvider/StoreProvider";
import RouterProvider from "components/RouterProvider/RouterProvider";
import ProjectCard from "components/Projects/ProjectCard";
import { ProjectType } from "store/modules/projects/projects.types";
import { ProjectsReducerType } from "store/modules/projects/projects.types";
import { useFormatMessage } from "components/IntlProvider";
import AnimatedPlaceholder from "components/AnimatedPlaceholder/AnimatedPlaceholder";

const { ROUTES, Redirect } = RouterProvider;
const { connect, selectors, actions } = StoreProvider;

interface OrganizationProjectsProps
  extends RouteComponentProps<{ codename: string }> {
  projects: ProjectsReducerType["list"];
  setProjects: (payload: ProjectsReducerType["list"]) => void;
  clearProjects: () => void;
}

const OrganizationProjects = ({
  projects,
  setProjects,
  match: { params },
  clearProjects,
  location,
  history,
}: OrganizationProjectsProps) => {
  const t = useFormatMessage();
  const [isLoaded, setIsLoaded] = useState(false);

  const refreshProjectList = (
    codename: string,
    params?: {
      limit?: string;
      offset?: string;
      search?: string;
      status?: string;
    }
  ) => {
    setIsLoaded(false);
    return api.resources
      .getProjects({
        organizationCodename: codename,
        params,
      })
      .then(({ data }) => {
        if (params?.offset) {
          setProjects({
            ...data,
            results: [...projects.results, ...data.results],
          });
        } else {
          setProjects(data);
        }
      })
      .catch(() => {
        showToast({
          type: "error",
          title: t(
            "errors.toast.general",
            "Something went wrong, please contact administrator"
          ),
        });
      })
      .finally(() => {
        setIsLoaded(true);
      });
  };

  const loadMoreProjects = (nextUrl = "") => {
    const nextParsedUrl = new URL(nextUrl);
    const search = queryString.parse(nextParsedUrl.search);
    if (params.codename) {
      refreshProjectList(
        params.codename,
        search as {
          limit: string;
          offset: string;
          search: string;
          status: string;
        }
      );
    }
  };

  useEffect(() => {
    if (params.codename) {
      refreshProjectList(params.codename);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    return () => {
      clearProjects();
    };
    // eslint-disable-next-line
  }, []);

  if (!params.codename) {
    return <Redirect to={ROUTES.ADMIN.ORGANIZATIONS.LIST} />;
  }

  return (
    <Dashboard
      title={`Organization ${params.codename} projects`}
      location={location}
    >
      <BackTo onClick={() => history.push("/admin/organizations")}>
        {t("backTo.organizations", "Back to Organizations")}
      </BackTo>
      <AnimatedPlaceholder
        isLoaded={isLoaded}
        hasValues={projects.results.length > 0}
        text={t("noresults", "No results. We have nothing to show here.")}
      >
        <>
          {projects.results.map((project: ProjectType) => (
            <ProjectCard
              link={`${ROUTES.ADMIN.ORGANIZATIONS.LIST}/${params.codename}/projects/${project.id}/review`}
              key={project.id}
              status={project.status.value}
              id={project.id}
              name={project.name}
              brand={project.brand}
              createdAt={project.created_at}
              updatedAt={project.updated_at}
            />
          ))}
        </>
      </AnimatedPlaceholder>
      {projects.next !== null && (
        <Grid container justify="center">
          <Button
            variant="primary"
            onClick={() => loadMoreProjects(projects.next || "")}
          >
            {t("projects.list.more", "More")}
          </Button>
        </Grid>
      )}
    </Dashboard>
  );
};

export default connect(
  (state) => ({
    projects: selectors.projects.list(state),
  }),
  {
    setProjects: actions.projects.setProjects,
    clearProjects: actions.projects.clearProjects,
  }
)(OrganizationProjects);
